import { useCallback, useState } from 'react';
import { ReportDateRangePicker } from '@reports/presentation/components/base/ReportDateRangePicker';
import { ReportFiltersSheet } from '@reports/presentation/components/base/ReportFiltersSheet';
import type { AppliedFilters } from '@reports/presentation/components/base/ReportFiltersSheet';
import { ReportFilterBadges } from '@reports/presentation/components/base/ReportFilterBadges';
import { ReportExportButton } from '@reports/presentation/components/base/ReportExportButton';
import { useFilterCatalog } from '@reports/presentation/hooks/useFilterCatalog';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from '@reports/presentation/utils';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ReportDateRange } from '@reports/domain/value-objects';
import type { Report } from '@reports/domain/entities';

interface ReportFiltersBarProps {
  appliedFilters: BlockDataRequestDto;
  onFiltersChange: (filters: BlockDataRequestDto) => void;
  reportDefinition: Report;
  isExporting?: boolean;
  onExport?: () => void;
}

/**
 * ReportFiltersBar - Combined filters bar component
 *
 * Combines date range picker, filter sheet (opens drawer), and export button.
 * Manages filter state and integrates with useFilterCatalog hook.
 * Gets filter catalog from composition root (no apiPort prop needed).
 * Extracts filter configurations and report type from report definition internally.
 *
 * @param {ReportFiltersBarProps} props - Component properties
 * @param {BlockDataRequestDto} props.appliedFilters - Current applied filters
 * @param {Function} props.onFiltersChange - Callback when filters change
 * @param {Report} props.reportDefinition - Complete report definition with metadata, filters, and type
 * @param {boolean} [props.isExporting=false] - Whether export is in progress
 * @param {Function} [props.onExport] - Callback when export button is clicked
 * @returns {React.ReactElement} Filters bar component
 *
 * @example
 * <ReportFiltersBar
 *   appliedFilters={appliedFilters}
 *   onFiltersChange={updateFilters}
 *   reportDefinition={reportDefinition}
 *   onExport={handleExport}
 * />
 */
export const ReportFiltersBar = ({
  appliedFilters,
  onFiltersChange,
  reportDefinition,
  isExporting = false,
  onExport,
}: ReportFiltersBarProps) => {
  const filterConfigs = reportDefinition.filters;
  const reportType = reportDefinition.type;
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

  const { filterCatalogData, filterCatalogState } = useFilterCatalog({
    filterConfigs,
    reportType,
  });

  const handleDateRangeChange = useCallback(
    (dateRange: ReportDateRange) => {
      onFiltersChange({
        ...appliedFilters,
        startDate: dateRange.start,
        endDate: dateRange.end,
        preset: dateRange.preset,
      });
    },
    [appliedFilters, onFiltersChange],
  );

  const handleApplyFilters = useCallback(
    (sheetFilters: AppliedFilters) => {
      onFiltersChange({
        ...appliedFilters,
        startHour: sheetFilters.startHour,
        endHour: sheetFilters.endHour,
        filters: sheetFilters.filters,
      });
      setIsSheetOpen(false);
    },
    [appliedFilters, onFiltersChange],
  );

  const handleRemoveFilter = useCallback(
    (filterKey: string) => {
      const { [filterKey]: removed, ...updatedFilters } = appliedFilters.filters;
      onFiltersChange({
        ...appliedFilters,
        filters: updatedFilters,
      });
    },
    [appliedFilters, onFiltersChange],
  );

  const handleRemoveHours = useCallback(() => {
    onFiltersChange({
      ...appliedFilters,
      startHour: DEFAULT_START_HOUR,
      endHour: DEFAULT_END_HOUR,
    });
  }, [appliedFilters, onFiltersChange]);

  const handleBadgeClick = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  // Convert BlockDataRequestDto to ReportDateRange for the picker
  const dateRangeValue: ReportDateRange = {
    start: appliedFilters.startDate,
    end: appliedFilters.endDate,
    preset: appliedFilters.preset,
  };

  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="flex flex-col md:flex-row items-center gap-3">
        <ReportDateRangePicker
          isFiltersLoading={filterCatalogState.isFetching}
          onChange={handleDateRangeChange}
          value={dateRangeValue}
        />

        <ReportFiltersSheet
          dateRange={dateRangeValue}
          defaultEndHour={appliedFilters.endHour}
          defaultStartHour={appliedFilters.startHour}
          filterOptions={filterCatalogData}
          filtersSelected={appliedFilters.filters}
          isFiltersLoading={filterCatalogState.isFetching}
          onApply={handleApplyFilters}
          onOpenChange={setIsSheetOpen}
          open={isSheetOpen}
          reportDefinition={reportDefinition}
        />

        <div className="hidden md:flex flex-1" />

        <ReportExportButton disabled={filterCatalogState.isFetching} isLoading={isExporting} onClick={onExport} />
      </div>

      <ReportFilterBadges
        appliedFilters={appliedFilters}
        filterConfigs={filterConfigs}
        isFiltersLoading={filterCatalogState.isFetching}
        onBadgeClick={handleBadgeClick}
        onRemoveFilter={handleRemoveFilter}
        onRemoveHours={handleRemoveHours}
      />
    </div>
  );
};
